import type { AppRouter } from '@/app/routes/routes'
import { analyticsSentryIntegration } from '@/lib/analytics'
import { useAuthStore } from '@/stores/authStore'
import { DeploymentType, getDeploymentType } from '@/utils/deployment'
import * as Sentry from '@sentry/react'

const SENTRY_ENABLED_STORAGE_KEY = 'isSentryEnabled'

function isSentryEnabled(): boolean {
	try {
		const runtimeToggle = localStorage.getItem(SENTRY_ENABLED_STORAGE_KEY)
		if (runtimeToggle !== null) {
			return runtimeToggle === 'true'
		}
	} catch {
		// falls back to the deployment type below
	}

	// Off by default on a developer's own machine, on everywhere real traffic lands.
	return getDeploymentType() !== DeploymentType.LOCAL
}

let initialized = false

export function initSentry(router: AppRouter): void {
	if (initialized || !isSentryEnabled()) {
		return
	}

	const dsn = import.meta.env.VITE_SENTRY_DSN
	if (!dsn) {
		return
	}

	Sentry.init({
		dsn,
		release: import.meta.env.VITE_RELEASE,
		environment: getDeploymentType(),
		// Every custom domain is one environment, enterprise, so the host tells them apart.
		initialScope: { tags: { host: window.location.hostname } },
		// Names pageload and navigation spans by route id, so an org or device id never becomes a transaction.
		integrations: [
			Sentry.tanstackRouterBrowserTracingIntegration(router, {
				// One sampling decision per tab for the whole session, so a sampled user has every page.
				linkPreviousTrace: 'session-storage',
				consistentTraceSampling: true,
				// The attribution headers panelsApi already sends name the panel behind each request span.
				onRequestSpanStart: (span, { headers }) => {
					span.setAttributes({
						'dashboard.id': headers?.get('x-bytebeam-dashboard-id') ?? undefined,
						'panel.id': headers?.get('x-bytebeam-panel-id') ?? undefined,
					})
				},
			}),
			analyticsSentryIntegration(),
		],
		// Enterprise hosts are small enough to trace whole; console keeps a sample.
		tracesSampler: ({ inheritOrSampleWith }) =>
			inheritOrSampleWith(getDeploymentType() === DeploymentType.ENTERPRISE ? 1 : 0.1),
		// We call setSentryUser ourselves, so the SDK should not also auto populate user.* fields.
		dataCollection: { userInfo: false },
	})
	// The host tag reaches error events only; spans read scope attributes.
	Sentry.setAttribute('host', window.location.hostname)

	initialized = true
}

export function setSentryUser(email: string): void {
	if (!initialized) {
		return
	}
	Sentry.setUser({ email })
}

export function clearSentryUser(): void {
	if (!initialized) {
		return
	}
	Sentry.setUser(null)
}

export function setSentryTag(key: string, value: string | undefined): void {
	if (!initialized) {
		return
	}
	Sentry.setTag(key, value)
	// Tags reach error events only; spans read scope attributes.
	Sentry.setAttribute(key, value)
}

export function captureException(
	error: unknown,
	hint?: { tags?: Record<string, string | number | undefined>; extra?: Record<string, unknown> },
): void {
	if (!initialized) {
		return
	}
	Sentry.captureException(error, hint)
}

/**
 * Keeps Sentry's user and tags in step with the auth store for the life of the app. The store
 * rehydrates from localStorage before this runs, so each piece applies once immediately and again
 * on every later change, the same two step shape `startAccessSync` uses for the same reason.
 */
export function startSentryContextSync(): () => void {
	const applyUser: (user: ReturnType<typeof useAuthStore.getState>['user']) => void = user => {
		if (!user) {
			clearSentryUser()
			return
		}
		setSentryUser(user.email)
	}

	const applyOrganizationId = (organizationId: string | null) => {
		setSentryTag('organization_id', organizationId ?? undefined)
	}

	const applyTenantId = (tenantId: string) => {
		setSentryTag('tenant_id', tenantId || undefined)
	}

	const initialState = useAuthStore.getState()
	applyUser(initialState.user)
	applyOrganizationId(initialState.currentOrganizationId)
	applyTenantId(initialState.currentTenant)

	const stops = [
		useAuthStore.subscribe(state => state.user, applyUser),
		useAuthStore.subscribe(state => state.currentOrganizationId, applyOrganizationId),
		useAuthStore.subscribe(state => state.currentTenant, applyTenantId),
	]

	return () => {
		for (const stop of stops) {
			stop()
		}
	}
}
